import {
  useInboxNotifications,
  useMarkAllInboxNotificationsAsRead,
  useUnreadInboxNotificationsCount,
} from '@liveblocks/react/suspense';
import { InboxNotification, InboxNotificationList } from '@liveblocks/react-ui';
import {
  Popover,
  IconButton,
  Badge,
  Box,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import MailIcon from '@mui/icons-material/Mail'; // 알림용 아이콘
import { Suspense, useState, MouseEvent } from 'react';

export default function NotificationsPopover() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notifications-popover' : undefined;

  return (
    <Box sx={{ display: 'inline-block' }}>
      <IconButton
        aria-describedby={id}
        onClick={handleClick}
        size="small"
        sx={{ width: 32, height: 32 }}
      >
        <Suspense fallback={<MailIcon fontSize="small" />}>
          <UnreadNotificationsBadge />
        </Suspense>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 450,
            maxWidth: '90vw',
            minHeight: 200,
            maxHeight: 600,
            borderRadius: 3,
            mt: 1.5,
            boxShadow: '0px 10px 25px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          },
        }}
      >
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          }
        >
          <Inbox />
        </Suspense>
      </Popover>
    </Box>
  );
}

function UnreadNotificationsBadge() {
  const { count } = useUnreadInboxNotificationsCount();

  return (
    <Badge
      badgeContent={count}
      color="primary"
      sx={{
        '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 },
      }}
    >
      <MailIcon fontSize="small" />
    </Badge>
  );
}

function Inbox() {
  const { inboxNotifications } = useInboxNotifications();
  const markAllNotificationsAsRead = useMarkAllInboxNotificationsAsRead();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* 헤더 섹션 */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          justifyContent: 'flex-end',
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          size="small"
          variant="contained"
          disabled={inboxNotifications.length === 0}
          onClick={() => markAllNotificationsAsRead()}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Mark all as read
        </Button>
      </Box>

      {/* 리스트 섹션 */}
      <Box sx={{ overflowY: 'auto', maxHeight: 500 }}>
        {inboxNotifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <InboxNotificationList>
            {inboxNotifications.map((inboxNotification) => (
              <InboxNotification
                key={inboxNotification.id}
                inboxNotification={inboxNotification}
              />
            ))}
          </InboxNotificationList>
        )}
      </Box>
    </Box>
  );
}
